import type { FormField } from '../../../types';
import { normalizeConversationSections, type ConversationSection } from '../../../lib/conversation-sections';

/** Fields and grouping metadata are committed together. */
export interface BuilderDefinition { fields: FormField[]; sections: ConversationSection[] }

/** Inclusive bounds keep an Information block and its attached answer inseparable. */
export function builderFieldBounds(fields: readonly FormField[], index: number): [number, number] {
  const field = fields[index];
  if (field?.informationFieldId && fields[index - 1]?.id === field.informationFieldId && fields[index - 1]?.type === 'information') return [index - 1, index];
  if (field?.type === 'information' && fields[index + 1]?.informationFieldId === field.id) return [index, index + 1];
  return [index, index];
}

/** Preserve empty headings at their existing location when the last field is removed. */
export function reconcileBuilderSections(previousFields: readonly FormField[], fields: readonly FormField[], sections: readonly ConversationSection[]): ConversationSection[] {
  const ids = new Set(fields.map(field => field.id));
  return normalizeConversationSections(fields, sections.map(section => {
    const fieldIds = section.fieldIds.filter(id => ids.has(id));
    const { beforeFieldId: _anchor, ...metadata } = section;
    if (fieldIds.length) return { ...metadata, fieldIds };
    const originalIndex = previousFields.findIndex(field => field.id === (section.fieldIds[0] ?? section.beforeFieldId));
    const beforeFieldId = originalIndex < 0 ? undefined : previousFields.slice(originalIndex).find(field => ids.has(field.id) && !section.fieldIds.includes(field.id))?.id;
    return { ...metadata, fieldIds, ...(beforeFieldId ? { beforeFieldId } : {}) };
  }));
}

/** Insert new questions into the plus control's owner, including an empty section. */
export function insertSectionFields(fields: readonly FormField[], sections: readonly ConversationSection[], insertIndex: number, additions: FormField[], ownerId?: string): BuilderDefinition {
  const [, end] = builderFieldBounds(fields, insertIndex - 1);
  const position = Math.min(fields.length, Math.max(0, end >= insertIndex ? end + 1 : insertIndex));
  const next = [...fields]; next.splice(position, 0, ...additions);
  const owner = ownerId ?? sections.find(section => section.fieldIds.includes(fields[position - 1]?.id))?.id;
  const nextSections = sections.map(section => section.id === owner ? { ...section, fieldIds: [...section.fieldIds, ...additions.map(field => field.id)] } : section);
  return { fields: next, sections: normalizeConversationSections(next, nextSections) };
}

/** A section begins at the chosen plus and owns the following questions in that group. */
export function addBuilderSection(fields: readonly FormField[], sections: readonly ConversationSection[], insertIndex: number, id: string, ownerId?: string): BuilderDefinition {
  const [, end] = builderFieldBounds(fields, insertIndex - 1);
  const position = Math.min(fields.length, Math.max(0, end >= insertIndex ? end + 1 : insertIndex));
  const owner = sections.find(section => section.id === ownerId) ?? sections.find(section => section.fieldIds.includes(fields[position - 1]?.id));
  const membership = new Set(sections.flatMap(section => section.fieldIds));
  const suffix = owner ? fields.slice(position).filter(field => owner.fieldIds.includes(field.id))
    : fields.slice(position, fields.findIndex((field, i) => i >= position && membership.has(field.id)) < 0 ? fields.length : fields.findIndex((field, i) => i >= position && membership.has(field.id)));
  const suffixIds = new Set(suffix.map(field => field.id));
  const section: ConversationSection = { id, title: 'Untitled section', fieldIds: [...suffixIds], ...(!suffix.length && fields[position] ? { beforeFieldId: fields[position].id } : {}) };
  const nextSections = sections.map(candidate => candidate.id === owner?.id ? { ...candidate, fieldIds: candidate.fieldIds.filter(fieldId => !suffixIds.has(fieldId)), ...(!candidate.fieldIds.some(fieldId => !suffixIds.has(fieldId)) && fields[position] ? { beforeFieldId: fields[position].id } : {}) } : candidate);
  nextSections.splice(owner ? nextSections.findIndex(candidate => candidate.id === owner.id) + 1 : nextSections.length, 0, section);
  return { fields: [...fields], sections: normalizeConversationSections(fields, nextSections) };
}

/** Move the complete question group to an explicit section boundary on release. */
export function moveSectionFields(fields: readonly FormField[], sections: readonly ConversationSection[], fieldId: string, targetSectionId?: string, beforeFieldId?: string): BuilderDefinition {
  const index = fields.findIndex(field => field.id === fieldId);
  if (index < 0) return { fields: [...fields], sections: [...sections] };
  const [start, end] = builderFieldBounds(fields, index);
  const moved = fields.slice(start, end + 1); const movedIds = new Set(moved.map(field => field.id));
  const sourceOwner = sections.find(section => section.fieldIds.includes(fieldId));
  if (beforeFieldId && movedIds.has(beforeFieldId) && sourceOwner?.id === targetSectionId) return { fields: [...fields], sections: [...sections] };
  const target = sections.find(section => section.id === targetSectionId);
  let boundary = beforeFieldId;
  if (!boundary && target) {
    const lastIndex = Math.max(...target.fieldIds.map(id => fields.findIndex(field => field.id === id)), -1);
    boundary = lastIndex >= 0 ? fields[lastIndex + 1]?.id : target.beforeFieldId;
  }
  if (boundary && movedIds.has(boundary)) boundary = fields[end + 1]?.id;
  const next = fields.filter(field => !movedIds.has(field.id));
  let position = boundary ? next.findIndex(field => field.id === boundary) : next.length;
  if (position < 0) position = next.length;
  if (boundary) position = builderFieldBounds(next, position)[0];
  next.splice(position, 0, ...moved);
  const memberships = sections.map(section => {
    const fieldIds = [...section.fieldIds.filter(id => !movedIds.has(id)), ...(section.id === targetSectionId ? moved.map(field => field.id) : [])];
    if (fieldIds.length) return { ...section, fieldIds };
    const startIndex = fields.findIndex(field => field.id === (section.fieldIds[0] ?? section.beforeFieldId));
    const before = startIndex < 0 ? undefined : fields.slice(startIndex).find(field => !movedIds.has(field.id))?.id;
    const { beforeFieldId: _anchor, ...metadata } = section;
    return { ...metadata, fieldIds, ...(before ? { beforeFieldId: before } : {}) };
  });
  return { fields: next, sections: normalizeConversationSections(next, memberships) };
}

/** Reorder a complete group relative to another section while preserving ungrouped fields. */
export function moveBuilderSection(fields: readonly FormField[], sections: readonly ConversationSection[], from: number, to: number): BuilderDefinition {
  if (from === to || !sections[from] || !sections[to]) return { fields: [...fields], sections: [...sections] };
  const source = sections[from]; const target = sections[to];
  const movedIds = new Set(source.fieldIds);
  const moved = fields.filter(field => movedIds.has(field.id));
  const next = fields.filter(field => !movedIds.has(field.id));
  const targetIndices = target.fieldIds.map(id => next.findIndex(field => field.id === id));
  const anchorIndex = target.beforeFieldId ? next.findIndex(field => field.id === target.beforeFieldId) : next.length;
  const position = targetIndices.length ? (from < to ? Math.max(...targetIndices) + 1 : Math.min(...targetIndices)) : Math.max(0, anchorIndex);
  const anchor = next[position]?.id;
  next.splice(position, 0, ...moved);
  const nextSections = sections.map(section => {
    if (section.id === target.id && !section.fieldIds.length && from < to && moved.length) return { ...section, beforeFieldId: moved[0].id };
    if (section.fieldIds.length || !section.beforeFieldId || !movedIds.has(section.beforeFieldId)) return section;
    const oldPosition = fields.findIndex(field => field.id === section.beforeFieldId);
    const beforeFieldId = fields.slice(oldPosition).find(field => !movedIds.has(field.id))?.id;
    const { beforeFieldId: _previousAnchor, ...metadata } = section;
    return { ...metadata, ...(beforeFieldId ? { beforeFieldId } : {}) };
  });
  nextSections.splice(from, 1);
  nextSections.splice(to, 0, { ...source, ...(!moved.length ? { beforeFieldId: anchor } : {}) });
  return { fields: next, sections: normalizeConversationSections(next, nextSections) };
}
